import { useState, useRef } from "react";
import { toast } from "sonner";
import { Trash2, Plus, Pencil, Upload } from "lucide-react";
import { adminApi, apiError } from "@/lib/api";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const inputCls = "border border-slate-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400";

const emptyForm = {
  name: "", location: "", boarding: "", price_adult: "", price_child: "",
  min_group: "1", min_age: "0", capacity: "50", days_of_week: [], times: "", image: "",
};

const toForm = (t) => ({
  name: t.name, location: t.location, boarding: t.boarding || "",
  price_adult: String(t.price_adult), price_child: t.price_child == null ? "" : String(t.price_child),
  min_group: String(t.min_group), min_age: String(t.min_age), capacity: String(t.capacity),
  days_of_week: [...t.days_of_week], times: t.times.join("\n"), image: t.image || "",
});

const TourForm = ({ form, setForm, onSave, onCancel, busy, testPrefix }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const toggleDay = (d) =>
    setForm({ ...form, days_of_week: form.days_of_week.includes(d) ? form.days_of_week.filter((x) => x !== d) : [...form.days_of_week, d] });
  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await adminApi().post("/admin/tours/upload-image", fd);
      setForm((f) => ({ ...f, image: data.url }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  return (
    <div className="bg-sky-50 border border-sky-200 rounded-xl p-5 space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input data-testid={`${testPrefix}-name`} className={inputCls} placeholder="Tour name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <input data-testid={`${testPrefix}-location`} className={inputCls} placeholder="Meeting point / location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
        <input data-testid={`${testPrefix}-boarding`} className={inputCls} placeholder="Boarding note (e.g. Boarding at 2:30 PM)" value={form.boarding} onChange={(e) => setForm({ ...form, boarding: e.target.value })} />
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid={`${testPrefix}-upload-photo`}
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-2 bg-slate-900 hover:bg-slate-700 text-white text-sm font-bold px-4 py-2 rounded-xl transition-colors duration-150 disabled:opacity-60 shrink-0"
          >
            <Upload size={14} /> {uploading ? "Uploading…" : "Upload Photo"}
          </button>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={uploadImage} data-testid={`${testPrefix}-photo-file-input`} />
          {form.image ? (
            <img src={form.image} alt="Tour" className="h-10 w-16 object-cover rounded-lg border border-slate-300" data-testid={`${testPrefix}-photo-preview`} />
          ) : (
            <span className="text-xs text-slate-400">No photo yet</span>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <input data-testid={`${testPrefix}-price-adult`} type="number" min="1" className={inputCls} placeholder="Adult $" value={form.price_adult} onChange={(e) => setForm({ ...form, price_adult: e.target.value })} />
        <input data-testid={`${testPrefix}-price-child`} type="number" min="0" className={inputCls} placeholder="Kids $ (blank = same)" value={form.price_child} onChange={(e) => setForm({ ...form, price_child: e.target.value })} />
        <input data-testid={`${testPrefix}-min-group`} type="number" min="1" className={inputCls} placeholder="Min group" value={form.min_group} onChange={(e) => setForm({ ...form, min_group: e.target.value })} />
        <input data-testid={`${testPrefix}-min-age`} type="number" min="0" className={inputCls} placeholder="Min age (0 = none)" value={form.min_age} onChange={(e) => setForm({ ...form, min_age: e.target.value })} />
        <input data-testid={`${testPrefix}-capacity`} type="number" min="1" className={inputCls} placeholder="Capacity" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} />
      </div>
      <div>
        <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Days the tour runs</p>
        <div className="flex flex-wrap gap-2">
          {DAYS.map((label, d) => (
            <button
              key={d}
              type="button"
              data-testid={`${testPrefix}-day-${label.toLowerCase()}`}
              onClick={() => toggleDay(d)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-colors duration-150 ${
                form.days_of_week.includes(d) ? "bg-sky-600 text-white" : "bg-white border border-slate-300 text-slate-500"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Time slots (one per line)</p>
        <textarea
          data-testid={`${testPrefix}-times`}
          rows={2}
          className={`w-full resize-none ${inputCls}`}
          placeholder={"10:00 AM – 12:00 PM\n1:00 PM – 3:00 PM"}
          value={form.times}
          onChange={(e) => setForm({ ...form, times: e.target.value })}
        />
      </div>
      <div className="flex gap-3">
        <button data-testid={`${testPrefix}-save`} onClick={onSave} disabled={busy} className="btn-ocean text-white text-sm font-bold px-6 py-2.5 rounded-xl disabled:opacity-60">
          Save Tour
        </button>
        <button onClick={onCancel} className="text-sm font-bold text-slate-500 hover:text-slate-700 px-3">Cancel</button>
      </div>
    </div>
  );
};

export const ToursManager = ({ tours, reload }) => {
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);

  const payload = () => ({
    name: form.name.trim(),
    location: form.location.trim(),
    boarding: form.boarding.trim(),
    price_adult: parseFloat(form.price_adult),
    price_child: form.price_child === "" ? null : parseFloat(form.price_child),
    min_group: parseInt(form.min_group, 10),
    min_age: parseInt(form.min_age, 10),
    capacity: parseInt(form.capacity, 10),
    days_of_week: form.days_of_week,
    times: form.times.split("\n").map((t) => t.trim()).filter(Boolean),
    image: form.image.trim(),
  });

  const validate = () => {
    const p = payload();
    if (!p.name || !p.location) return "Name and location are required";
    if (isNaN(p.price_adult) || p.price_adult <= 0) return "Enter a valid adult price";
    if (p.days_of_week.length === 0) return "Select at least one day";
    if (p.times.length === 0) return "Add at least one time slot";
    return null;
  };

  const save = async () => {
    const err = validate();
    if (err) return toast.error(err);
    setBusy(true);
    try {
      if (adding) await adminApi().post("/admin/tours", payload());
      else await adminApi().put(`/admin/tours/${editing}`, payload());
      toast.success(adding ? "Tour added" : "Tour updated");
      setEditing(null); setAdding(false); setForm(emptyForm);
      reload();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.name}"? Customers will no longer be able to book it.`)) return;
    try {
      await adminApi().delete(`/admin/tours/${t.id}`);
      toast.success("Tour deleted");
      reload();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6">
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-display text-lg font-bold text-slate-900">Tours</h2>
        <button
          data-testid="admin-add-tour-button"
          onClick={() => { setAdding(true); setEditing(null); setForm(emptyForm); }}
          className="btn-ocean flex items-center gap-2 text-white text-sm font-bold px-4 py-2 rounded-xl"
        >
          <Plus size={15} /> Add Tour
        </button>
      </div>
      <p className="text-sm text-slate-500 mb-5">Edit pricing, schedule, capacity and rules. Changes apply to new bookings immediately.</p>
      {adding && <div className="mb-5"><TourForm form={form} setForm={setForm} onSave={save} onCancel={() => setAdding(false)} busy={busy} testPrefix="tour-new" /></div>}
      <div className="space-y-3">
        {tours.map((t) => (
          <div key={t.id} className="bg-slate-50 rounded-xl px-4 py-3" data-testid={`admin-tour-${t.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm font-bold text-slate-800">{t.name}</p>
                <p className="text-xs text-slate-500">
                  {t.schedule} · {t.location} · ${t.price_adult}{t.price_child != null ? ` / $${t.price_child} kids` : ""} · min {t.min_group} · cap {t.capacity}{t.min_age > 0 ? ` · ${t.min_age}+` : ""}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  data-testid={`admin-edit-tour-${t.id}`}
                  onClick={() => { setEditing(editing === t.id ? null : t.id); setAdding(false); setForm(toForm(t)); }}
                  className="flex items-center gap-1.5 text-sm font-bold text-sky-600 hover:text-sky-700"
                >
                  <Pencil size={14} /> {editing === t.id ? "Close" : "Edit"}
                </button>
                <button data-testid={`admin-delete-tour-${t.id}`} onClick={() => remove(t)} className="text-slate-400 hover:text-red-500 transition-colors duration-150">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
            {editing === t.id && (
              <div className="mt-4">
                <TourForm form={form} setForm={setForm} onSave={save} onCancel={() => setEditing(null)} busy={busy} testPrefix={`tour-edit-${t.id}`} />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
